import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Building2, Calendar, FileText, Users, ExternalLink, 
  CheckCircle2, XCircle, Hash, MapPin, Lock
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NotesBlock from '@/components/NotesBlock';
import CallLogCard from '@/components/CallLogCard';
import CallLogModal from '@/components/modals/CallLogModal';
import { toast } from 'sonner';

export default function EntityProfile() {
  const { clientId, entityId } = useParams();
  const { client } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [entity, setEntity] = useState(null);
  const [individuals, setIndividuals] = useState([]);
  const [advisers, setAdvisers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCallLogModal, setShowCallLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !clientId || !entityId) return;
      try {
        const [entRes, indRes, advRes, callLogsRes] = await Promise.all([
          api.getEntity(token, clientId, entityId),
          api.getIndividuals(token, clientId),
          api.getAdvisers(token, clientId),
          api.getCallLogs(token, clientId)
        ]);
        setEntity(entRes.data);
        setIndividuals(indRes.data);
        setAdvisers(advRes.data);
        // Filter call logs for this entity
        setCallLogs(callLogsRes.data.filter(log => 
          log.linkedEntityId === entityId
        ));
      } catch (error) {
        console.error('Failed to fetch entity:', error);
        navigate(`/app/clients/${clientId}/entities`);
      }
      setLoading(false);
    };
    fetchData();
  }, [token, clientId, entityId, navigate]);

  const handleSaveNotes = async (notes) => {
    try {
      const response = await api.updateEntityNotes(token, clientId, entityId, notes);
      setEntity(response.data);
      toast.success('Notes saved');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const handleAddCallLog = async (callLogData) => {
    try {
      const response = await api.createCallLog(token, clientId, {
        ...callLogData,
        linkedEntityId: entityId
      });
      setCallLogs([response.data, ...callLogs]);
      toast.success('Call logged successfully');
    } catch (error) {
      console.error('Failed to create call log:', error);
      toast.error('Failed to log call');
    }
  };

  // Get linked individuals
  const linkedIndividuals = individuals.filter(ind => 
    entity?.linkedPeople?.includes(ind.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="empty-state">
        <Building2 className="w-12 h-12" />
        <p className="text-lg font-medium text-[#5D524D]">Entity not found</p>
      </div>
    );
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'Company': return 'bg-[#4A3B32]/10 text-[#4A3B32]';
      case 'Trust': return 'bg-[#6B8E6B]/10 text-[#6B8E6B]';
      case 'SMSF': return 'bg-[#D99A6C]/10 text-[#D99A6C]';
      case 'Foundation': return 'bg-[#7D8C9E]/10 text-[#7D8C9E]';
      default: return 'bg-[#F0EBE6] text-[#5D524D]';
    }
  };

  return (
    <div className="animate-fade-in-up" data-testid="entity-profile">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(`/app/clients/${clientId}/entities`)}
        className="caffeine-btn-ghost mb-4"
        data-testid="back-to-entities-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Entities & Roles
      </Button>

      {/* Entity Header */}
      <div className="caffeine-card p-6 mb-6">
        <div className="flex items-start gap-6">
          <div className="w-16 h-16 bg-[#4A3B32]/10 rounded-lg flex items-center justify-center">
            <Building2 className="w-8 h-8 text-[#4A3B32]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="font-heading text-2xl text-[#2C2420]">{entity.name}</h1>
              <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(entity.type)}`}>
                {entity.type}
              </span>
              {entity.authoritySet ? (
                <span className="badge-verified">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Authority Set
                </span>
              ) : (
                <span className="badge-broken">
                  <XCircle className="w-3 h-3 mr-1" />
                  Authority Missing
                </span>
              )}
            </div>
            <p className="text-[#5D524D]">{entity.purpose}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registration Details */}
        <div className="caffeine-card p-6">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">Registration Details</h2>
          <div className="space-y-4">
            {entity.abn && (
              <div className="flex items-start gap-3">
                <Hash className="w-5 h-5 text-[#8C8078] mt-0.5" />
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">ABN</p>
                  <p className="text-[#2C2420] font-mono">{entity.abn}</p>
                </div>
              </div>
            )}
            
            {entity.acn && (
              <div className="flex items-start gap-3">
                <Hash className="w-5 h-5 text-[#8C8078] mt-0.5" />
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">ACN</p>
                  <p className="text-[#2C2420] font-mono">{entity.acn}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Establishment Date</p>
                <p className="text-[#2C2420]">{formatDateAU(entity.establishmentDate)}</p>
              </div>
            </div>
            
            {entity.vestingDate && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#8C8078] mt-0.5" />
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Vesting Date</p>
                  <p className="text-[#2C2420]">{formatDateAU(entity.vestingDate)}</p>
                </div>
              </div>
            )}
            
            {entity.registeredOffice && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#8C8078] mt-0.5" />
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Registered Office</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="masked-field cursor-help">
                          <Lock className="w-3.5 h-3.5" />
                          •••••••••••••
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="sensitive-tooltip">
                        Sensitive fields are permission-controlled. Request access in live product.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
          </div>

          {/* Open in source system buttons */}
          <div className="mt-6 pt-4 border-t border-[#E6E0D9] space-y-2">
            <Button variant="ghost" className="w-full caffeine-btn-ghost justify-start">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in ASIC
            </Button>
            <Button variant="ghost" className="w-full caffeine-btn-ghost justify-start">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in ATO Portal
            </Button>
            <p className="text-xs text-[#8C8078] text-center mt-2">Indexed, not stored</p>
          </div>
        </div>

        {/* Type-specific Details */}
        <div className="caffeine-card p-6">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">
            {entity.type === 'Company' ? 'Company Details' :
             entity.type === 'Trust' || entity.type === 'Unit Trust' ? 'Trust Details' :
             entity.type === 'SMSF' ? 'SMSF Details' :
             entity.type === 'Foundation' ? 'Foundation Details' : 'Details'}
          </h2>
          
          {/* Company */}
          {entity.type === 'Company' && (
            <div className="space-y-4">
              {entity.directors && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Directors</p>
                  <div className="space-y-2">
                    {entity.directors.map((dir, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-[#F9F7F5] rounded">
                        <Users className="w-4 h-4 text-[#4A3B32]" />
                        <span className="text-[#2C2420]">{dir}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {entity.shareholders && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Shareholders</p>
                  <div className="space-y-2">
                    {entity.shareholders.map((sh, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-[#F9F7F5] rounded">
                        <span className="text-[#2C2420]">{sh.name}</span>
                        <span className="text-sm text-[#4A3B32] font-medium">{sh.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trust */}
          {(entity.type === 'Trust' || entity.type === 'Unit Trust') && (
            <div className="space-y-4">
              {entity.trustee && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Trustee</p>
                  <p className="text-[#2C2420]">{entity.trustee}</p>
                </div>
              )}
              {entity.appointor && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Appointor</p>
                  <p className="text-[#2C2420]">{entity.appointor}</p>
                </div>
              )}
              {entity.beneficiaries && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Beneficiaries</p>
                  <p className="text-[#2C2420]">{entity.beneficiaries}</p>
                </div>
              )}
              {entity.unitholders && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Unitholders</p>
                  <div className="space-y-2">
                    {entity.unitholders.map((uh, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-[#F9F7F5] rounded">
                        <span className="text-[#2C2420]">{uh.name}</span>
                        <span className="text-sm text-[#4A3B32] font-medium">{uh.units} units</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button variant="ghost" className="w-full caffeine-btn-ghost justify-start mt-4">
                <FileText className="w-4 h-4 mr-2" />
                Open Trust Deed in source system
              </Button>
            </div>
          )}

          {/* SMSF */}
          {entity.type === 'SMSF' && (
            <div className="space-y-4">
              {entity.members && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Members</p>
                  <div className="space-y-2">
                    {entity.members.map((mem, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-[#F9F7F5] rounded">
                        <Users className="w-4 h-4 text-[#4A3B32]" />
                        <span className="text-[#2C2420]">{mem}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {entity.trustees && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Trustees</p>
                  <div className="space-y-2">
                    {entity.trustees.map((tr, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-[#F9F7F5] rounded">
                        <Building2 className="w-4 h-4 text-[#4A3B32]" />
                        <span className="text-[#2C2420]">{tr}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Foundation */}
          {entity.type === 'Foundation' && (
            <div className="space-y-4">
              {entity.directors && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Directors</p>
                  <div className="space-y-2">
                    {entity.directors.map((dir, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-[#F9F7F5] rounded">
                        <Users className="w-4 h-4 text-[#4A3B32]" />
                        <span className="text-[#2C2420]">{dir}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {entity.trustees && (
                <div>
                  <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-2">Trustee</p>
                  <div className="space-y-2">
                    {entity.trustees.map((tr, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-[#F9F7F5] rounded">
                        <Building2 className="w-4 h-4 text-[#4A3B32]" />
                        <span className="text-[#2C2420]">{tr}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        <NotesBlock
          notes={entity?.notes || entity?.purpose}
          updatedAt={entity?.notesUpdatedAt}
          updatedBy={entity?.notesUpdatedBy}
          onSave={handleSaveNotes}
          title="Notes / Purpose"
          placeholder={`Add notes about ${entity?.name}...`}
        />

        {/* Call Log */}
        <CallLogCard
          callLogs={callLogs}
          onViewAll={() => navigate(`/app/clients/${clientId}/activity`)}
          onAddNew={() => setShowCallLogModal(true)}
          maxDisplay={3}
          title={`Call Log - ${entity?.name}`}
        />

        {/* Linked People */}
        <div className="caffeine-card p-6">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">Linked People</h2>
          {linkedIndividuals.length > 0 ? (
            <div className="space-y-3">
              {linkedIndividuals.map(ind => (
                <div 
                  key={ind.id}
                  className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg cursor-pointer hover:bg-[#F0EBE6] transition-colors"
                  onClick={() => navigate(`/app/clients/${clientId}/individuals/${ind.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm">
                      {ind.firstName[0]}{ind.lastName[0]}
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2420]">{ind.firstName} {ind.lastName}</p>
                      <p className="text-xs text-[#8C8078]">{ind.role}</p>
                    </div>
                  </div>
                  <Users className="w-4 h-4 text-[#8C8078]" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#8C8078]">No linked people</p>
          )}
        </div>
      </div>

      {/* Call Log Modal */}
      <CallLogModal
        isOpen={showCallLogModal}
        onClose={() => setShowCallLogModal(false)}
        onSave={handleAddCallLog}
        individuals={individuals}
        advisers={advisers}
      />
    </div>
  );
}
